import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, StatusBadge, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import EvidencePreviewModal from "../components/EvidencePreviewModal";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Upload, Users, Calendar, FileText, CheckSquare,
  ScrollText, Sparkles, ArrowLeft, Save, Download, Loader2, Package,
} from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return s; }
}

export default function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = () => api.get(`/cases/${id}`).then((r) => setC(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!c) return <div className="label-eyebrow">Loading case…</div>;
  const tone = { Active: "active", Closed: "neutral", Pending: "warn", Intake: "neutral" }[c.status] || "neutral";

  return (
    <div data-testid={`case-detail-${id}`}>
      <button onClick={() => navigate("/app/cases")} className="text-sm text-[#4A5568] hover:text-[#2A0F5A] inline-flex items-center gap-1 mb-6" data-testid="back-to-cases-btn">
        <ArrowLeft size={14} /> All cases
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="label-eyebrow text-[#C5A059] mb-2">{c.case_type} · {c.jurisdiction}</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight font-medium">{c.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <StatusBadge tone={tone}>{c.status}</StatusBadge>
            {c.case_number && <span className="text-xs text-[#4A5568]">No. {c.case_number}</span>}
            <span className="text-xs text-[#4A5568]">Updated {fmtDate(c.updated_at)}</span>
          </div>
        </div>
        <FilingPackButton caseId={id} caseTitle={c.title} />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="w-full justify-start flex-wrap h-auto bg-transparent p-0 border-b border-[#E8E3F0] rounded-none gap-6">
          {[
            ["overview", "Overview"],
            ["facts", "Facts"],
            ["parties", "Parties"],
            ["claims", "Claims & Defences"],
            ["evidence", "Evidence"],
            ["chronology", "Timeline"],
            ["hearings", "Hearings"],
            ["communications", "Comms"],
            ["drafts", "Drafts"],
            ["tasks", "Deadlines"],
          ].map(([v, l]) => (
            <TabsTrigger
              key={v}
              value={v}
              className="rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-[#2A0F5A] data-[state=active]:shadow-none data-[state=active]:bg-transparent bg-transparent px-0 pb-3 text-sm"
              data-testid={`case-tab-${v}`}
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-8">
          <TabsContent value="overview"><OverviewTab caseData={c} reload={load} /></TabsContent>
          <TabsContent value="facts"><FactsTab caseId={id} caseData={c} reload={load} /></TabsContent>
          <TabsContent value="parties"><PartiesTab caseData={c} reload={load} /></TabsContent>
          <TabsContent value="claims"><ClaimsTab caseId={id} caseData={c} reload={load} /></TabsContent>
          <TabsContent value="chronology"><ChronologyTab caseId={id} /></TabsContent>
          <TabsContent value="evidence"><EvidenceTab caseId={id} /></TabsContent>
          <TabsContent value="hearings"><HearingsTab caseId={id} caseData={c} reload={load} /></TabsContent>
          <TabsContent value="communications"><CommunicationsTab caseId={id} caseData={c} reload={load} /></TabsContent>
          <TabsContent value="drafts"><DraftsTab caseId={id} /></TabsContent>
          <TabsContent value="tasks"><TasksTab caseId={id} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// ---------- Overview ----------
function OverviewTab({ caseData, reload }) {
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [desc, setDesc] = useState(caseData.description || "");
  const [editing, setEditing] = useState(false);

  const summarize = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/ai/summarize-case/${caseData.id}`);
      setSummary(data.summary);
    } catch {
      toast.error("Summary failed");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    await api.patch(`/cases/${caseData.id}`, { description: desc });
    toast.success("Saved");
    setEditing(false);
    reload();
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 border border-[#E8E3F0] bg-white p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">What happened</div>
          {!editing ? (
            <Button variant="ghost" size="sm" className="rounded-none" onClick={() => setEditing(true)} data-testid="edit-desc-btn">Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="rounded-none" onClick={() => { setDesc(caseData.description || ""); setEditing(false); }}>Cancel</Button>
              <Button size="sm" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={save} data-testid="save-desc-btn"><Save size={12} className="mr-1" /> Save</Button>
            </div>
          )}
        </div>
        {editing ? (
          <Textarea rows={8} value={desc} onChange={(e) => setDesc(e.target.value)} className="rounded-none border-[#E8E3F0]" data-testid="desc-textarea" />
        ) : (
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{caseData.description || <span className="text-[#4A5568] italic">No description yet.</span>}</p>
        )}
      </div>
      <div className="border border-[#E8E3F0] bg-white p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">AI Summary</div>
          <Button size="sm" variant="outline" className="rounded-none border-[#E8E3F0]" onClick={summarize} disabled={loading} data-testid="ai-summarize-btn">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            <span className="ml-1.5">Summarize</span>
          </Button>
        </div>
        <div className="text-sm leading-relaxed text-[#2A0F5A]/80 whitespace-pre-wrap min-h-[80px]">
          {summary || <span className="text-[#4A5568] italic">Ask Marianne to summarize this case.</span>}
        </div>
      </div>
    </div>
  );
}

// ---------- Parties ----------
function PartiesTab({ caseData, reload }) {
  const [parties, setParties] = useState(caseData.parties || []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "plaintiff", email: "", phone: "", address: "", notes: "" });

  const add = async () => {
    if (!form.name) return toast.error("Name is required");
    const next = [...parties, { ...form, id: crypto.randomUUID() }];
    await api.patch(`/cases/${caseData.id}`, { parties: next });
    setParties(next);
    setOpen(false);
    setForm({ name: "", role: "plaintiff", email: "", phone: "", address: "", notes: "" });
    toast.success("Party added");
    reload();
  };

  const remove = async (pid) => {
    const next = parties.filter((p) => p.id !== pid);
    await api.patch(`/cases/${caseData.id}`, { parties: next });
    setParties(next);
    reload();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Parties ({parties.length})</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="add-party-btn">
              <Plus size={12} className="mr-1" /> Add party
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Add a party</DialogTitle>
              <DialogDescription>Who is involved in this matter?</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="label-eyebrow mb-2 block">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="party-name-input" />
              </div>
              <div>
                <Label className="label-eyebrow mb-2 block">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-none border-[#E8E3F0]" data-testid="party-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["plaintiff", "defendant", "witness", "counsel", "other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-[#E8E3F0]" />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none border-[#E8E3F0]" />
              </div>
              <Textarea rows={2} placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none border-[#E8E3F0]" />
              <Textarea rows={2} placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none border-[#E8E3F0]" />
            </div>
            <DialogFooter>
              <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={add} data-testid="party-save-btn">Add party</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {parties.length === 0 ? (
        <EmptyState icon={Users} title="No parties yet" description="Add the people and organisations involved in this matter." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {parties.map((p) => (
            <div key={p.id} className="border border-[#E8E3F0] bg-white p-5" data-testid={`party-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-serif text-xl">{p.name}</div>
                  <StatusBadge tone="neutral">{p.role}</StatusBadge>
                </div>
                <button onClick={() => remove(p.id)} className="text-[#4A5568] hover:text-red-700" data-testid={`remove-party-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              {(p.email || p.phone) && (
                <div className="mt-3 text-xs text-[#4A5568] space-y-0.5">
                  {p.email && <div>{p.email}</div>}
                  {p.phone && <div>{p.phone}</div>}
                </div>
              )}
              {p.address && <div className="mt-2 text-xs whitespace-pre-wrap">{p.address}</div>}
              {p.notes && <div className="mt-3 text-sm text-[#2A0F5A]/75 whitespace-pre-wrap">{p.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Chronology ----------
function ChronologyTab({ caseId }) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ event_date: "", title: "", description: "" });

  const load = () => api.get(`/chronology/case/${caseId}`).then((r) => setEvents(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const add = async () => {
    if (!form.event_date || !form.title) return toast.error("Date and title required");
    await api.post("/chronology", { case_id: caseId, ...form, evidence_ids: [] });
    toast.success("Event added");
    setOpen(false);
    setForm({ event_date: "", title: "", description: "" });
    load();
  };

  const remove = async (eid) => {
    await api.delete(`/chronology/${eid}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Chronology ({events.length})</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="add-event-btn">
              <Plus size={12} className="mr-1" /> Add event
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Add a chronology event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="label-eyebrow mb-2 block">Date</Label>
                <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="event-date-input" />
              </div>
              <div>
                <Label className="label-eyebrow mb-2 block">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="event-title-input" />
              </div>
              <div>
                <Label className="label-eyebrow mb-2 block">Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="event-desc-input" />
              </div>
            </div>
            <DialogFooter>
              <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={add} data-testid="event-save-btn">Add event</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {events.length === 0 ? (
        <EmptyState icon={Calendar} title="Timeline empty" description="Build a chronology of the matter, event by event." />
      ) : (
        <ol className="border-l border-[#E8E3F0] ml-3 pl-6 space-y-8">
          {events.map((e) => (
            <li key={e.id} className="relative" data-testid={`event-${e.id}`}>
              <span className="absolute -left-[29px] top-1.5 w-2.5 h-2.5 bg-[#C5A059]" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-xs tracking-widest text-[#C5A059]">{fmtDate(e.event_date)}</div>
                  <div className="font-serif text-xl mt-1">{e.title}</div>
                  {e.description && <div className="text-sm text-[#2A0F5A]/75 mt-2 whitespace-pre-wrap max-w-2xl">{e.description}</div>}
                </div>
                <button onClick={() => remove(e.id)} className="text-[#4A5568] hover:text-red-700" data-testid={`remove-event-${e.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------- Evidence ----------
function EvidenceTab({ caseId }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => api.get(`/evidence/case/${caseId}`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("case_id", caseId);
    fd.append("description", description);
    fd.append("tags", tags);
    fd.append("file", file);
    try {
      await api.post("/evidence/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidence uploaded");
      setDescription("");
      setTags("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (item) => {
    const res = await api.get(`/evidence/${item.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = item.original_filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const remove = async (id) => {
    await api.delete(`/evidence/${id}`);
    load();
  };

  return (
    <div>
      <div className="border border-[#E8E3F0] bg-white p-6 mb-6">
        <div className="label-eyebrow mb-3">Upload evidence</div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <Input placeholder="Short description" value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-none border-[#E8E3F0]" data-testid="evidence-desc-input" />
          <Input placeholder="Tags (comma separated)" value={tags} onChange={(e) => setTags(e.target.value)} className="rounded-none border-[#E8E3F0]" data-testid="evidence-tags-input" />
        </div>
        <label className="inline-block">
          <input type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="evidence-file-input" />
          <span className={`inline-flex items-center gap-2 px-4 py-2 cursor-pointer text-sm text-[#FAFAF8] ${uploading ? "bg-[#4A5568]" : "bg-[#2A0F5A] hover:bg-[#3D1F85]"}`}>
            <Upload size={14} /> {uploading ? "Uploading…" : "Choose file"}
          </span>
        </label>
      </div>
      {items.length === 0 ? (
        <EmptyState icon={ScrollText} title="Evidence vault empty" description="Upload PDFs, images, audio or video that supports your case." />
      ) : (
        <div className="border border-[#E8E3F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E3F0]">
                <th className="text-left px-4 py-3 label-eyebrow">File</th>
                <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Tags</th>
                <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Uploaded</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr
                  key={i.id}
                  className="border-b border-[#E8E3F0] hover:bg-[#FAFAF8] cursor-pointer"
                  onClick={() => setPreview(i)}
                  data-testid={`evidence-${i.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{i.original_filename}</div>
                    <div className="text-xs text-[#4A5568]">{(i.size / 1024).toFixed(1)} KB · {i.content_type}</div>
                    {i.description && <div className="text-xs text-[#2A0F5A]/70 mt-0.5">{i.description}</div>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs">{i.tags?.join(", ")}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-[#4A5568]">{fmtDate(i.created_at)}</td>
                  <td className="px-4 py-3 text-right space-x-3" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => download(i)} className="text-[#4A5568] hover:text-[#2A0F5A]" data-testid={`evidence-dl-${i.id}`}><Download size={14} /></button>
                    <button onClick={() => remove(i.id)} className="text-[#4A5568] hover:text-red-700" data-testid={`evidence-del-${i.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EvidencePreviewModal evidence={preview} onClose={() => setPreview(null)} onUpdated={load} />
    </div>
  );
}

// ---------- Drafts ----------
function DraftsTab({ caseId }) {
  const [drafts, setDrafts] = useState([]);
  useEffect(() => {
    api.get(`/drafts`, { params: { case_id: caseId } }).then((r) => setDrafts(r.data));
  }, [caseId]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Drafts ({drafts.length})</div>
        <Link to={`/app/templates?case=${caseId}`}>
          <Button size="sm" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="new-draft-from-template-btn">
            <FileText size={12} className="mr-1" /> Draft from template
          </Button>
        </Link>
      </div>
      {drafts.length === 0 ? (
        <EmptyState icon={FileText} title="No drafts yet" description="Use a template to generate a polished first draft." />
      ) : (
        <div className="space-y-3">
          {drafts.map((d) => (
            <Link key={d.id} to={`/app/drafts/${d.id}`} className="block border border-[#E8E3F0] bg-white p-5 hover-lift" data-testid={`draft-link-${d.id}`}>
              <div className="font-serif text-xl">{d.title}</div>
              <div className="text-xs text-[#4A5568] mt-1">Updated {fmtDate(d.updated_at)}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Tasks (per case) ----------
function TasksTab({ caseId }) {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "normal" });

  const load = () => api.get("/tasks", { params: { case_id: caseId } }).then((r) => setTasks(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const add = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/tasks", { ...form, case_id: caseId });
    toast.success("Task added");
    setOpen(false);
    setForm({ title: "", description: "", due_date: "", priority: "normal" });
    load();
  };

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/tasks/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Deadlines & tasks ({tasks.length})</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="add-task-btn">
              <Plus size={12} className="mr-1" /> New task
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-serif text-2xl">New task</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="task-title-input" />
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="task-due-input" />
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-none border-[#E8E3F0]"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "normal", "high"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <Textarea rows={2} placeholder="Notes" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-[#E8E3F0]" />
            </div>
            <DialogFooter>
              <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={add} data-testid="task-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {tasks.length === 0 ? (
        <EmptyState icon={CheckSquare} title="No tasks" description="Track replies, hearings, and filings." />
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => (
            <li key={t.id} className={`border border-[#E8E3F0] bg-white p-4 flex items-center gap-3 ${t.status === "done" ? "opacity-60" : ""}`} data-testid={`task-${t.id}`}>
              <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} className="w-4 h-4" data-testid={`task-check-${t.id}`} />
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${t.status === "done" ? "line-through" : ""}`}>{t.title}</div>
                <div className="text-xs text-[#4A5568] mt-0.5">
                  {t.due_date && `Due ${fmtDate(t.due_date)}`} · {t.priority}
                </div>
              </div>
              <button onClick={() => remove(t.id)} className="text-[#4A5568] hover:text-red-700" data-testid={`remove-task-${t.id}`}><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilingPackButton({ caseId, caseTitle }) {
  const [busy, setBusy] = React.useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("marianne_token");
      const res = await fetch(`${BACKEND}/api/cases/${caseId}/filing-pack`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `marianne-filing-pack-${(caseTitle || "matter").replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Filing Pack downloaded.");
    } catch (e) {
      toast.error(e?.message || "Could not build the filing pack.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={run}
      disabled={busy}
      className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
      data-testid="filing-pack-btn"
    >
      {busy ? (
        <><Loader2 size={14} className="mr-1.5 animate-spin" /> Building…</>
      ) : (
        <><Package size={14} className="mr-1.5" /> Download Filing Pack</>
      )}
    </Button>
  );
}


// ---------- Facts tab (the story so far) ------------------------------------
function FactsTab({ caseId, caseData, reload }) {
  const [facts, setFacts] = useState(caseData?.facts || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setFacts(caseData?.facts || ""); }, [caseData?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/cases/${caseId}/facts`, { facts });
      toast.success("Facts saved");
      reload && reload();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="case-facts-tab" className="max-w-3xl">
      <div className="label-eyebrow mb-1.5">The story so far</div>
      <p className="text-sm text-[#4A5568] mb-4">
        Write a clean, chronological narrative of what happened. These facts are the foundation of every claim, pleading and hearing note that follows.
      </p>
      <Textarea
        value={facts}
        onChange={(e) => setFacts(e.target.value)}
        placeholder="On 15 Jan 2026 I purchased a washing machine from Acme for ₹35,000. On 29 Jan 2026 the machine stopped running. Despite three service calls over the following 10 days…"
        rows={16}
        className="rounded-none border-[#E8E3F0] font-serif text-[15px] leading-relaxed"
        data-testid="facts-textarea"
      />
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={save} disabled={saving} className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="save-facts-btn">
          {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> Save facts</>}
        </Button>
        <span className="text-xs text-[#4A5568]">{facts.length.toLocaleString()} characters</span>
      </div>
    </div>
  );
}

// ---------- Claims & Defences -----------------------------------------------
function ClaimsTab({ caseId, caseData, reload }) {
  const [items, setItems] = useState(caseData?.claims || []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", cause_of_action: "", status: "open", defence: "", notes: "" });

  const load = async () => {
    const r = await api.get(`/cases/${caseId}/claims`);
    setItems(r.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const add = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      await api.post(`/cases/${caseId}/claims`, form);
      setForm({ title: "", cause_of_action: "", status: "open", defence: "", notes: "" });
      setAdding(false);
      await load(); reload && reload();
      toast.success("Claim added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this claim?")) return;
    await api.delete(`/cases/${caseId}/claims/${id}`);
    await load(); reload && reload();
    toast.success("Removed");
  };

  const setStatus = async (id, status) => {
    await api.patch(`/cases/${caseId}/claims/${id}`, { status });
    await load();
  };

  const tone = { open: "neutral", contested: "warn", withdrawn: "neutral", won: "success", lost: "danger", settled: "neutral" };

  return (
    <div data-testid="case-claims-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">{items.length} claim{items.length === 1 ? "" : "s"}</div>
        {!adding && (
          <Button onClick={() => setAdding(true)} variant="outline" className="rounded-none border-[#E8E3F0] uppercase tracking-widest text-xs" data-testid="add-claim-btn">
            <Plus size={14} className="mr-2" /> Add claim
          </Button>
        )}
      </div>

      {adding && (
        <div className="border border-[#E8E3F0] bg-white p-5 mb-6 space-y-3" data-testid="add-claim-form">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Claim title (e.g. Deficiency of service)" className="rounded-none border-[#E8E3F0]" data-testid="claim-title-input" />
          <Textarea value={form.cause_of_action} onChange={(e) => setForm({ ...form, cause_of_action: e.target.value })} placeholder="Cause of action — the legal basis (e.g. Section 2(11) Consumer Protection Act, 2019)" rows={2} className="rounded-none border-[#E8E3F0]" data-testid="claim-cause-input" />
          <Textarea value={form.defence} onChange={(e) => setForm({ ...form, defence: e.target.value })} placeholder="Defence (anticipated or actual)" rows={2} className="rounded-none border-[#E8E3F0]" />
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows={2} className="rounded-none border-[#E8E3F0]" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setAdding(false)} className="rounded-none border-[#E8E3F0]">Cancel</Button>
            <Button onClick={add} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="submit-claim-btn">Save claim</Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={ScrollText} title="No claims yet" description="Add the specific legal claims that make up this case — each with its own cause of action, status, and defence."
          action={<Button onClick={() => setAdding(true)} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs"><Plus size={14} className="mr-2" /> Add claim</Button>}
        />
      ) : (
        <div className="space-y-3">
          {items.map((cl) => (
            <div key={cl.id} className="border border-[#E8E3F0] bg-white p-5" data-testid={`claim-${cl.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl leading-tight">{cl.title}</div>
                  {cl.cause_of_action && <div className="text-xs text-[#4A5568] mt-1 italic">{cl.cause_of_action}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <select value={cl.status} onChange={(e) => setStatus(cl.id, e.target.value)} className="border border-[#E8E3F0] px-2 py-1 text-xs bg-white" data-testid={`claim-status-${cl.id}`}>
                    {["open", "contested", "withdrawn", "won", "lost", "settled"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(cl.id)} className="text-[#4A5568] hover:text-[#8a2121]"><Trash2 size={14} /></button>
                </div>
              </div>
              {(cl.defence || cl.notes) && (
                <div className="mt-3 pt-3 border-t border-[#E8E3F0] text-sm text-[#2A0F5A]/90 space-y-2">
                  {cl.defence && <div><span className="label-eyebrow">Defence</span><p className="mt-1">{cl.defence}</p></div>}
                  {cl.notes && <div><span className="label-eyebrow">Notes</span><p className="mt-1 whitespace-pre-wrap">{cl.notes}</p></div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Hearings & Deadlines --------------------------------------------
function HearingsTab({ caseId, caseData, reload }) {
  const [items, setItems] = useState(caseData?.hearings || []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: "", title: "", court: "", status: "scheduled", judge: "", notes: "" });

  const load = async () => {
    const r = await api.get(`/cases/${caseId}/hearings`);
    setItems((r.data || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || "")));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const add = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Date and title required");
    try {
      await api.post(`/cases/${caseId}/hearings`, form);
      setForm({ date: "", title: "", court: "", status: "scheduled", judge: "", notes: "" });
      setAdding(false);
      await load(); reload && reload();
      toast.success("Hearing scheduled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this hearing?")) return;
    await api.delete(`/cases/${caseId}/hearings/${id}`);
    await load(); reload && reload();
  };

  const setStatus = async (id, status) => {
    await api.patch(`/cases/${caseId}/hearings/${id}`, { status });
    await load();
  };

  return (
    <div data-testid="case-hearings-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">{items.length} hearing{items.length === 1 ? "" : "s"}</div>
        {!adding && (
          <Button onClick={() => setAdding(true)} variant="outline" className="rounded-none border-[#E8E3F0] uppercase tracking-widest text-xs" data-testid="add-hearing-btn">
            <Plus size={14} className="mr-2" /> Add hearing
          </Button>
        )}
      </div>

      {adding && (
        <div className="border border-[#E8E3F0] bg-white p-5 mb-6 space-y-3" data-testid="add-hearing-form">
          <div className="grid grid-cols-2 gap-3">
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="hearing-date-input" />
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Hearing title / stage" className="rounded-none border-[#E8E3F0]" data-testid="hearing-title-input" />
          </div>
          <Input value={form.court} onChange={(e) => setForm({ ...form, court: e.target.value })} placeholder="Court / forum" className="rounded-none border-[#E8E3F0]" />
          <Input value={form.judge} onChange={(e) => setForm({ ...form, judge: e.target.value })} placeholder="Judge / officer" className="rounded-none border-[#E8E3F0]" />
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows={2} className="rounded-none border-[#E8E3F0]" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setAdding(false)} className="rounded-none border-[#E8E3F0]">Cancel</Button>
            <Button onClick={add} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="submit-hearing-btn">Save hearing</Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Calendar} title="No hearings scheduled" description="Track every scheduled, attended or adjourned appearance here — they also show up in the unified Calendar."
          action={<Button onClick={() => setAdding(true)} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs"><Plus size={14} className="mr-2" /> Add hearing</Button>}
        />
      ) : (
        <div className="space-y-2">
          {items.map((h) => (
            <div key={h.id} className="border border-[#E8E3F0] bg-white p-4 flex items-start gap-4" data-testid={`hearing-${h.id}`}>
              <div className="shrink-0 text-center min-w-[72px]">
                <div className="label-eyebrow text-[#C5A059]">{(h.date || "").slice(0, 7)}</div>
                <div className="font-serif text-3xl leading-none">{(h.date || "").slice(8, 10) || "—"}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-serif text-lg leading-tight">{h.title}</div>
                  <div className="flex items-center gap-2">
                    <select value={h.status} onChange={(e) => setStatus(h.id, e.target.value)} className="border border-[#E8E3F0] px-2 py-1 text-xs bg-white" data-testid={`hearing-status-${h.id}`}>
                      {["scheduled", "attended", "adjourned", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => remove(h.id)} className="text-[#4A5568] hover:text-[#8a2121]"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="text-xs text-[#4A5568] mt-1">
                  {[h.court, h.judge].filter(Boolean).join(" · ") || "—"}
                </div>
                {h.notes && <p className="text-sm mt-2 text-[#2A0F5A]/85 whitespace-pre-wrap">{h.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Communications ---------------------------------------------------
function CommunicationsTab({ caseId, caseData, reload }) {
  const [items, setItems] = useState(caseData?.communications || []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), channel: "email", subject: "", direction: "outbound", participants: "", notes: "" });

  const load = async () => {
    const r = await api.get(`/cases/${caseId}/communications`);
    setItems((r.data || []).slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const add = async () => {
    if (!form.subject.trim() || !form.date) return toast.error("Date and subject required");
    try {
      await api.post(`/cases/${caseId}/communications`, form);
      setForm({ date: new Date().toISOString().slice(0, 10), channel: "email", subject: "", direction: "outbound", participants: "", notes: "" });
      setAdding(false);
      await load(); reload && reload();
      toast.success("Logged");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this log entry?")) return;
    await api.delete(`/cases/${caseId}/communications/${id}`);
    await load(); reload && reload();
  };

  return (
    <div data-testid="case-communications-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">{items.length} log{items.length === 1 ? "" : "s"}</div>
        {!adding && (
          <Button onClick={() => setAdding(true)} variant="outline" className="rounded-none border-[#E8E3F0] uppercase tracking-widest text-xs" data-testid="add-comm-btn">
            <Plus size={14} className="mr-2" /> Log a comm
          </Button>
        )}
      </div>

      {adding && (
        <div className="border border-[#E8E3F0] bg-white p-5 mb-6 space-y-3" data-testid="add-comm-form">
          <div className="grid grid-cols-3 gap-3">
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="comm-date-input" />
            <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="border border-[#E8E3F0] px-3 py-2 text-sm bg-white" data-testid="comm-channel-select">
              {["email", "letter", "call", "meeting", "message", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="border border-[#E8E3F0] px-3 py-2 text-sm bg-white">
              <option value="outbound">outbound</option>
              <option value="inbound">inbound</option>
            </select>
          </div>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" className="rounded-none border-[#E8E3F0]" data-testid="comm-subject-input" />
          <Input value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} placeholder="Participants (comma-separated)" className="rounded-none border-[#E8E3F0]" />
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / summary" rows={3} className="rounded-none border-[#E8E3F0]" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setAdding(false)} className="rounded-none border-[#E8E3F0]">Cancel</Button>
            <Button onClick={add} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="submit-comm-btn">Log it</Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={ScrollText} title="No communications logged" description="Keep every email, letter, call and meeting here — one timeline, zero digging through inboxes." />
      ) : (
        <div className="border border-[#E8E3F0] bg-white divide-y divide-[#E8E3F0]">
          {items.map((c) => (
            <div key={c.id} className="px-5 py-4 flex items-start gap-4" data-testid={`comm-${c.id}`}>
              <div className="shrink-0 min-w-[76px] text-xs text-[#4A5568]">{c.date}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="label-eyebrow text-[#C5A059]">{c.channel}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#4A5568]">{c.direction}</span>
                </div>
                <div className="font-serif text-base leading-tight">{c.subject}</div>
                {c.participants && <div className="text-xs text-[#4A5568] mt-0.5">with {c.participants}</div>}
                {c.notes && <p className="text-sm mt-2 whitespace-pre-wrap text-[#2A0F5A]/85">{c.notes}</p>}
              </div>
              <button onClick={() => remove(c.id)} className="text-[#4A5568] hover:text-[#8a2121]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
